import Button from "@mui/material/Button";
import { lightTextBody } from "constants/colors";
import { FORMAT_TIME } from "utils/timeUtils";
import {
  useFindDataDumpForTrendChart,
  useFocusRange,
  useFormatZoned,
} from "utils/customHooks/genericHooks";
import { FormatZonedFn } from "utils/customHooks/genericHooksTypes";
import { DataDumpAggregateType } from "types/types";
import {
  getOutputLossSum,
  useOutputsAndTargets,
  getAheadBehindColors,
  useFixedTargetsOverride,
  useAheadBehindMetrics,
  DEFAULT_NUM_AB_ROWS,
  DEFAULT_NONMETRIC_AB_ROWS,
  handleABSpecialMetrics,
} from "../aheadBehindUtils";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { Skeleton } from "@mui/material";
import { ClockDataCellStyles } from "./ClockDataCellStyles";

type ClockDataCellProps = {
  startTime: number;
  endTime: number;
  units?: string;
  isSizeSmall?: boolean;
};

export default function ClockDataCell({
  startTime,
  endTime,
  isSizeSmall = false,
}) {
  /** An hourly data dump, which comes from the trend chart data dumps when binSize = 1 hour.
   * The binSize is set where data dumps are retrieved on the Operator page
   */
  const dataDump = useFindDataDumpForTrendChart({
    geToFind: startTime,
  });

  return (
    <ClockDataCellContent
      {...{
        startTime,
        endTime,
        isSizeSmall,
        dataDump,
      }}
    />
  );
}

function ClockDataCellContent({
  startTime,
  endTime,
  isSizeSmall = false,
  dataDump,
}: ClockDataCellProps & {
  dataDump: DataDumpAggregateType | null;
}) {
  const processId = useProcessIdOnSingleProcessPage();
  const [flightConfig] = useFlightConfig();
  const { currentFixedTarget } = useFixedTargetsOverride(dataDump, null);
  const targetScale =
    flightConfig.general.aheadBehindScaleTargetRateColorOnly[
      processId ?? "ALL"
    ] ??
    flightConfig.general.aheadBehindScaleTargetRateColorOnly["ALL"] ??
    flightConfig.general.aheadBehindScaleTargetRate[processId ?? "ALL"] ??
    flightConfig.general.aheadBehindScaleTargetRate["ALL"] ??
    1;
  const outputLossSum = getOutputLossSum(
    targetScale,
    dataDump,
    currentFixedTarget ?? undefined
  );

  const now = new Date().getTime();
  const isFutureRange = startTime > now;
  const isCurrent = startTime <= now && now < endTime;
  const { backgroundColor: buttonBackgroundColor, textColor: buttonTextColor } =
    getAheadBehindColors({ outputLossSum, isFutureRange });
  const outputLossTextColor =
    buttonBackgroundColor === "white" ? lightTextBody : buttonBackgroundColor;
  const formatZoned: FormatZonedFn = useFormatZoned();

  const startTimeFormatted = formatZoned(startTime, FORMAT_TIME);

  const teamSize = dataDump?.lastSegment?.teamSize ?? null;

  return (
    <ClockDataCellUI
      {...{
        startTimeFormatted,
        isCurrent,
        outputLossTextColor,
        buttonBackgroundColor,
        buttonTextColor,
        isSizeSmall,
        isFutureRange,
        teamSize,
        dataDump,
      }}
    />
  );
}

export function ClockDataCellUI({
  startTimeFormatted,
  isCurrent,
  outputLossTextColor,
  buttonBackgroundColor,
  buttonTextColor,
  isSizeSmall,
  isFutureRange,
  // teamSize,
  dataDump,
}: {
  startTimeFormatted: string;
  isCurrent: boolean;
  outputLossTextColor: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  isSizeSmall: boolean;
  isFutureRange: boolean;
  // teamSize: number | null;
  dataDump?: DataDumpAggregateType | null;
}) {
  const { translate } = useFlightTranslation();
  const {
    operatorPageOnlyMetrics_Shift,
    operatorPageOnlyMetrics_Hour,
    metricsLoading,
  } = useAheadBehindMetrics(dataDump ?? null);
  const focusRange = useFocusRange();

  const processId = useProcessIdOnSingleProcessPage();
  const {
    currentFixedTarget: currentFixedHourTarget,
    fixedTarget: totalFixedHourTarget,
  } = useFixedTargetsOverride(dataDump ?? null, null);

  const {
    productionPotentialOutput: hourCurrentTargetOutput,
    netOutput: hourOutput,
    outputLossSum: hourOutputLossSum,
    // totalPotentialOutput: hourTotalTargetOutput,
  } = useOutputsAndTargets(
    dataDump ?? null,
    processId,
    currentFixedHourTarget,
    totalFixedHourTarget
  );

  const hourCurrentTarget = currentFixedHourTarget ?? hourCurrentTargetOutput;
  const hourCount = hourOutput;
  // const hourTotalTarget = totalFixedHourTarget ?? hourTotalTargetOutput;

  return (
    <ClockDataCellStyles
      {...{
        $outputLossTextColor: outputLossTextColor,
        $buttonBackgroundColor: buttonBackgroundColor,
        $buttonTextColor: buttonTextColor,
        $isSizeSmall: isSizeSmall,
        $isCurrent: isCurrent,
        $isFutureRange: isFutureRange,
      }}
      style={{
        height:
          ((operatorPageOnlyMetrics_Shift.length + DEFAULT_NONMETRIC_AB_ROWS) /
            DEFAULT_NUM_AB_ROWS) *
          150,
      }}
    >
      <Button variant="contained" className="clockTime">
        {!focusRange ? "00:00" : startTimeFormatted}
      </Button>
      {/*
      {aheadBehindShowHeadCount && (
        <div className="headCount">{teamSize ?? "--"}</div>
      )} */}
      {operatorPageOnlyMetrics_Hour.map(metric => {
        const metricValue =
          dataDump && metric
            ? handleABSpecialMetrics({
                metric,
                target: hourCurrentTarget,
                count: hourCount,
                aheadBehind: hourOutputLossSum,
                dataDump,
              })
            : metric.value;

        const fallback =
          metric.dataDumpKey === "outputLossSum" && !isFutureRange ? 0 : "--";
        let metricValueDisplay =
          metricValue?.toFixed(metric.decimalPoints ?? 0) ?? fallback;

        // add a "+" before any ahead-behind positive value
        if (
          metric.dataDumpKey === "outputLossSum" &&
          metric.value &&
          metric.value > 0
        ) {
          metricValueDisplay = "+" + metricValueDisplay;
        }

        return (
          <div
            key={metric.dataDumpKey}
            className={`metric metric-${metric.dataDumpKey}`}
          >
            {metricsLoading && !isFutureRange ? "--" : metricValueDisplay}
          </div>
        );
      })}
      {isCurrent && (
        <div className="currentIndicator">{translate("Current")}</div>
      )}
      {!focusRange ? (
        <Skeleton
          variant="rectangular"
          style={{
            position: "absolute",
            inset: 0,
            height: "100%",
            borderRadius: 6,
          }}
        />
      ) : null}
    </ClockDataCellStyles>
  );
}
